import { gql, useMutation } from "@apollo/client"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";

import { ApiKeysQuery, CreateWebhookEndpointMutation, UpdateWebhookEndpointMutation, WebhookEndpointEventTypeEnum } from "~/graphql/__generated__/graphql";

// Temporary code; the enum, currently contains the unimplemented PatientUpdate event,
// which we don't want to display to users. When implementing PatientUpdate (or adding a new)
// event on top of that, strip this out and just use the enum values directly.
export const allowedEventTypes = [WebhookEndpointEventTypeEnum.VisitUpdate];

type Props = {
  apiKeys: ApiKeysQuery["apiKey"];
  endpoint: Partial<ApiKeysQuery["webhookEndpoint"][number]>;
  existingEndpoints: ApiKeysQuery["webhookEndpoint"];
  onClose: () => void;
}

const EditWebhookEndpoint = ({ endpoint, onClose, apiKeys, existingEndpoints }: Props) => {
  const [local, setLocal] = useState<Props["endpoint"]>(endpoint);
  const [createWebhookEndpoint, { loading: createLoading }] = useMutation<CreateWebhookEndpointMutation>(CREATE_WEBHOOK_ENDPOINT_MUTATION);
  const [updateWebhookEndpoint, { loading: updateLoading }] = useMutation<UpdateWebhookEndpointMutation>(UPDATE_WEBHOOK_ENDPOINT_MUTATION);

  const loading = createLoading || updateLoading;
  const urlValid = !!local.url?.match(/https?:\/\/.+/);

  return <Dialog
    fullWidth
    maxWidth="xs"
    open={true}
    onClose={onClose}
  >
    <DialogTitle>{local.id ? "Update" : "Add"} Webhook Endpoint</DialogTitle>
    <DialogContent>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Event Type</InputLabel>
          <Select
            value={local.eventType || ""}
            label="Event Type"
            onChange={(e) => setLocal({...local, eventType: e.target.value as WebhookEndpointEventTypeEnum})}
            required
            disabled={!!local.id}
          >
            {allowedEventTypes.map(value => <MenuItem
              value={value}
              key={value}
              disabled={existingEndpoints.some(e => e.eventType === value && e.id !== local.id)}
            >
              {value}
            </MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          value={local.url || ""}
          onChange={(e) => setLocal({...local, url: e.target.value})}
          required
          label="URL"
          error={!!local.url && !urlValid}
          fullWidth
          placeholder={"https://"}
        />
        <FormControl fullWidth>
          <InputLabel>Api Key</InputLabel>
          <Select
            value={(local.apiKeyId || "").toString()}
            onChange={(e) => setLocal({...local, apiKeyId: parseInt(e.target.value!)})}
            required
            label="API Key"
            fullWidth
          >
            {apiKeys.filter(key => key.keyPurpose === "webhook").map(key => <MenuItem value={key.id} key={key.id}>{key.apiKey}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button disabled={loading} onClick={onClose}>Cancel</Button>
      <Button loading={loading} disabled={!local.eventType || !local.url || !local.apiKeyId || !urlValid} type="submit" onClick={async () => {
        if (local.id) {
          await updateWebhookEndpoint({ variables: local, awaitRefetchQueries: true, refetchQueries: ["ApiKeys"] });
        } else {
          await createWebhookEndpoint({ variables: local, awaitRefetchQueries: true, refetchQueries: ["ApiKeys"] });
        }
        onClose();
      }}>
        {local.id ? "Update" : "Add"}
      </Button>
    </DialogActions>
  </Dialog>
}

export default EditWebhookEndpoint;

const CREATE_WEBHOOK_ENDPOINT_MUTATION = gql`
  mutation CreateWebhookEndpoint($apiKeyId: Int!, $eventType: WebhookEndpointEventTypeEnum!, $url: String!)
  @permission(requires: manageApiKeys) {
    createWebhookEndpoint(apiKeyId: $apiKeyId, eventType: $eventType, url: $url) {
      id
      apiKeyId
      eventType
      url
    }
  }
`;

const UPDATE_WEBHOOK_ENDPOINT_MUTATION = gql`
  mutation UpdateWebhookEndpoint($id: Int!, $apiKeyId: Int, $url: String)
  @permission(requires: manageApiKeys) {
    updateWebhookEndpoint(id:$id, apiKeyId: $apiKeyId, url: $url) {
      id
      apiKeyId
      eventType
      url
    }
  }
`;
